import { BaseApiClient } from "../api/baseApiClient.service";
import {
	closeCheckoutWindow,
	navigateCheckoutWindow,
} from "../stripe/checkoutWindow";

/**
 * A coin pack as defined in Stripe. Identified by its Stripe price id, since
 * Stripe (not our database) owns the catalog.
 */
export interface CoinPack {
	priceId: string;
	productId: string;
	name: string;
	coins: number;
	price: number;
	currency: string;
	/** Cheapest per coin — rendered as "Best value". */
	highlight: boolean;
}

export interface CoinPacksResponse {
	success: boolean;
	/** False when the user has no active subscription — show plans instead of packs. */
	eligible: boolean;
	subscriptionStatus: string | null;
	packs: CoinPack[];
}

interface CoinPackCheckoutResponse {
	success: boolean;
	sessionId?: string;
	url?: string;
	error?: string;
	code?: string;
}

/**
 * Raised when the backend rejects a top-up because the user has no active
 * subscription. Callers route the user to the plan picker instead.
 */
export class TopUpNotEligibleError extends Error {
	constructor() {
		super("An active subscription is required to buy coin packs");
		this.name = "TopUpNotEligibleError";
	}
}

/**
 * One-time coin top-ups for users who already have an active subscription.
 *
 * Unlike a plan change, a pack adds coins to the current balance and leaves the
 * subscription untouched, so there is no upgrade/downgrade branching here.
 */
export class CoinPackService {
	private static readonly BASE_PATH = "stripe";

	static async getPacks(): Promise<CoinPacksResponse> {
		return BaseApiClient.getInstance().get<CoinPacksResponse>(
			`${CoinPackService.BASE_PATH}/coin-packs`,
		);
	}

	/**
	 * Open Stripe Checkout for a pack.
	 *
	 * `preOpenedWindow` exists because Safari blocks `window.open` once the
	 * click handler has awaited a request; the caller opens a blank window
	 * synchronously and we navigate it with `navigateCheckoutWindow`.
	 */
	static async purchasePack(
		priceId: string,
		preOpenedWindow?: Window | null,
	): Promise<Window | null> {
		let response: CoinPackCheckoutResponse;

		try {
			response = await BaseApiClient.getInstance().post<
				CoinPackCheckoutResponse,
				{ priceId: string }
			>(`${CoinPackService.BASE_PATH}/create-coin-pack-checkout`, { priceId });
		} catch (error) {
			closeCheckoutWindow(preOpenedWindow ?? null);
			const status = (error as { response?: { status?: number } })?.response
				?.status;
			if (status === 403) {
				throw new TopUpNotEligibleError();
			}
			throw error;
		}

		if (!response.success || !response.url) {
			closeCheckoutWindow(preOpenedWindow ?? null);
			throw new Error(response.error || "Failed to create checkout session");
		}

		return navigateCheckoutWindow(preOpenedWindow ?? null, response.url);
	}
}
